import { distinctBy, distinctByKeyWithCondition } from '../utils/collections/distinctBy';
import { groupBy } from '../utils/collections/groupBy';
import { mapNotNull, mapToProp, mapToProps } from '../utils/collections/map';
import { maxBy, maxOf } from '../utils/collections/max';

export {};

declare global {
  interface Array<T> {
    groupBy(keyFn: (key: T) => string): Record<string, T[]>;
    distinctBy<K extends keyof T>(key: K): T[];
    distinctByKeyWithCondition<K extends keyof T, S extends Omit<T, K>>(key: K, ifDuplicate: (_: S) => boolean): T[];
    mapNotNull<U>(fn: (item: T) => U | null | undefined): U[];
    mapToProp<P extends keyof T>(key: P, includeFalsy?: boolean): Array<T[P]>;
    mapToProps<P extends keyof T>(keys: P[], includeFalsy?: boolean): Array<Pick<T, P>>;
    maxBy(fn: (_: T) => number): T | undefined;
    maxOf(fn: (_: T) => number): number | undefined;
  }
}

// Extend the Array prototype with groupBy method
Array.prototype.groupBy = function <T>(this: T[], keyFn: (key: T) => string): Record<string, T[]> {
  return groupBy(this, keyFn);
};

// Extend the Array prototype with distictBy and distinctByKeyWithCondition methods
Array.prototype.distinctBy = function <T, K extends keyof T>(key: K): T[] {
  return distinctBy(this, key);
};

Array.prototype.distinctByKeyWithCondition = function <T, K extends keyof T, S extends Omit<T, K>>(
  this: T[],
  key: K,
  ifDuplicate: (_: S) => boolean
): T[] {
  return distinctByKeyWithCondition(this, key, ifDuplicate);
};

// Extend the Array prototype with map methods
Array.prototype.mapNotNull = function <T, U>(this: T[], fn: (item: T) => U | null | undefined): U[] {
  return mapNotNull(this, fn);
};
Array.prototype.mapToProp = function <T, P extends keyof T>(this: T[], key: P, includeFalsy: boolean = true): Array<T[P]> {
  return mapToProp(this, key, includeFalsy);
};
Array.prototype.mapToProps = function <T, P extends keyof T>(this: T[], keys: P[], includeFalsy: boolean = true): Array<Pick<T, P>> {
  return mapToProps(this, keys, includeFalsy);
};

// Extend the Array prototype
Array.prototype.maxBy = function <T>(fn: (item: T) => number): T | undefined {
  return maxBy(this, fn);
};

Array.prototype.maxOf = function <T>(fn: (item: T) => number): number | undefined {
  return maxOf(this, fn);
};
